import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router';
import { motion } from 'framer-motion';
import { useAuth } from '../../contexts/AuthContext';
import { useCms } from '../../contexts/CmsContext';
import LeadCapturePopup from '../../components/layout/LeadCapturePopup';
import LeadCaptureFab from '../../components/layout/LeadCaptureFab';
import LeadCaptureBanner from '../../components/layout/LeadCaptureBanner';
import type { Course, CourseModule, Faq } from '../../types/course';
import Button from '../../components/ui/Button';
import {
  CheckCircle,
  Play,
  Clock,
  BookOpen,
  Star,
  ChevronDown,
  ChevronUp,
  IndianRupee,
  ArrowRight,
  Award,
  Users,
  Shield,
} from 'lucide-react';

interface Props {
  course: Course;
}

function Reveal({ children, delay = 0 }: { children: React.ReactNode; delay?: number }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: '-50px' }}
      transition={{ duration: 0.6, delay, ease: [0.22, 1, 0.36, 1] }}
    >
      {children}
    </motion.div>
  );
}

export default function FeaturedCourseLanding({ course }: Props): React.JSX.Element {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const cms = useCms();
  const [openModule, setOpenModule] = useState<number | null>(null);
  const [openFaq, setOpenFaq] = useState<number | null>(null);

  const safeArray = <T,>(value: any): T[] => {
    if (Array.isArray(value)) return value;
    if (typeof value === 'string') {
      try {
        const parsed = JSON.parse(value);
        return Array.isArray(parsed) ? parsed : [];
      } catch {
        return [];
      }
    }
    return [];
  };

  const modules: CourseModule[] = safeArray(course.modules);
  const faqs: Faq[] = safeArray(course.faq);
  const benefits: string[] = Array.isArray(course.benefits)
    ? course.benefits
    : (course.benefits ? JSON.parse(course.benefits) : []);
  const skills: string[] = Array.isArray(course.skills_covered)
    ? course.skills_covered
    : (course.skills_covered ? JSON.parse(course.skills_covered) : []);

  const price = Number(course.price ?? 0);
  const discountPrice = Number(course.discount_price ?? 0);
  const finalPrice = discountPrice > 0 && discountPrice < price ? discountPrice : price;

  const handleEnroll = () => {
    if (course.is_enrolled) {
      navigate(`/courses/${course.slug}`);
      return;
    }
    if (!isAuthenticated) {
      navigate('/login', { state: { from: `/enroll/${course.slug}` } });
      return;
    }
    navigate(`/enroll/${course.slug}`, { state: { course } });
  };

  const handleViewCourse = () => {
    navigate(`/courses/${course.slug}`);
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Hero */}
      <section className="relative pt-32 pb-20 px-4 overflow-hidden bg-slate-50">
        <div className="max-w-6xl mx-auto relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <Reveal>
                <span className="inline-block px-4 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider bg-amber-50 text-amber-500 mb-6">
                  {cms.get('featured_course_label', 'Featured Course')}
                </span>
              </Reveal>

              <Reveal delay={0.1}>
                <h1 className="text-4xl sm:text-5xl font-extrabold text-slate-900 leading-tight mb-6">
                  {course.title}
                </h1>
              </Reveal>

              <Reveal delay={0.2}>
                <p className="text-lg text-slate-600 mb-6 leading-relaxed">
                  {course.short_description}
                </p>
              </Reveal>

              <Reveal delay={0.25}>
                <div className="flex flex-wrap items-center gap-4 mb-6 text-sm text-slate-500">
                  {course.duration && (
                    <span className="flex items-center gap-1.5">
                      <Clock className="w-4 h-4" />
                      {course.duration}
                    </span>
                  )}
                  <span className="flex items-center gap-1.5">
                    <BookOpen className="w-4 h-4" />
                    {modules.length} Modules
                  </span>
                  <span className="flex items-center gap-1.5">
                    <Users className="w-4 h-4" />
                    {course.enrollment_count ?? 0} Enrolled
                  </span>
                  {course.trainer_name && (
                    <span className="flex items-center gap-1.5">
                      <Star className="w-4 h-4" />
                      by {course.trainer_name}
                    </span>
                  )}
                </div>
              </Reveal>

              <Reveal delay={0.3}>
                <div className="flex items-baseline gap-3 mb-6">
                  {discountPrice > 0 && discountPrice < price ? (
                    <>
                      <span className="text-3xl font-bold text-slate-900">₹{discountPrice.toLocaleString()}</span>
                      <span className="text-lg text-slate-400 line-through">₹{price.toLocaleString()}</span>
                      <span className="px-2 py-0.5 rounded-full bg-green-50 text-green-700 text-xs font-bold">
                        {Math.round((1 - discountPrice / price) * 100)}% OFF
                      </span>
                    </>
                  ) : (
                    <span className="text-3xl font-bold text-slate-900">₹{price.toLocaleString()}</span>
                  )}
                </div>
              </Reveal>

              <Reveal delay={0.35}>
                <div className="flex flex-col sm:flex-row gap-3">
                  <Button onClick={handleEnroll} size="lg" leftIcon={<IndianRupee className="w-4 h-4" />}>
                    {course.is_enrolled ? 'Continue Learning' : cms.get('featured_course_hero_cta', 'Enroll Now')}
                  </Button>
                  <Button onClick={handleViewCourse} variant="secondary" size="lg" leftIcon={<ArrowRight className="w-4 h-4" />}>
                    {cms.get('featured_course_secondary_cta', 'View Details')}
                  </Button>
                </div>
              </Reveal>
            </div>

            <Reveal delay={0.2}>
              <div className="relative">
                {course.course_image ? (
                  <img
                    src={course.course_image}
                    alt={course.title}
                    className="w-full rounded-3xl shadow-2xl shadow-slate-200/50 object-cover aspect-[4/3]"
                  />
                ) : (
                  <div className="w-full rounded-3xl bg-amber-100 aspect-[4/3] flex items-center justify-center">
                    <BookOpen className="w-20 h-20 text-amber-300" />
                  </div>
                )}
                {course.intro_video && (
                  <button className="absolute inset-0 flex items-center justify-center">
                    <div className="w-16 h-16 rounded-full bg-white/90 backdrop-blur flex items-center justify-center shadow-lg hover:scale-110 transition-transform">
                      <Play className="w-6 h-6 text-amber-500 ml-1" />
                    </div>
                  </button>
                )}
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* Benefits */}
      {benefits.length > 0 && (
        <section className="py-16 px-4 bg-white border-y border-slate-100">
          <div className="max-w-5xl mx-auto">
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {benefits.map((b, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.1 }}
                  className="flex items-start gap-3 p-4 rounded-xl bg-slate-50"
                >
                  <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                  <span className="text-sm text-slate-700 font-medium">{b}</span>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Curriculum */}
      {modules.length > 0 && (
        <section id="curriculum" className="py-20 px-4 bg-slate-50">
          <div className="max-w-4xl mx-auto">
            <Reveal>
              <div className="text-center mb-12">
                <span className="inline-block px-4 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider bg-amber-50 text-amber-500 mb-4">
                  Curriculum
                </span>
                <h2 className="text-3xl sm:text-4xl font-bold text-slate-900">What You Will Learn</h2>
              </div>
            </Reveal>

            <div className="space-y-3">
              {modules.map((m, i) => (
                <motion.div
                  key={m.id}
                  initial={{ opacity: 0, y: 10 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.05 }}
                  className="bg-white rounded-2xl border border-slate-200 overflow-hidden"
                >
                  <button
                    onClick={() => setOpenModule(openModule === m.id ? null : m.id)}
                    className="w-full flex items-center justify-between p-5 text-left hover:bg-slate-50 transition-colors"
                  >
                    <div className="flex items-center gap-4">
                      <span className="w-8 h-8 rounded-lg bg-amber-50 text-amber-600 flex items-center justify-center text-sm font-bold">
                        {i + 1}
                      </span>
                      <div>
                        <p className="font-semibold text-slate-900 text-sm">{m.title}</p>
                        {m.description && (
                          <p className="text-xs text-slate-500 mt-0.5 line-clamp-1">{m.description}</p>
                        )}
                      </div>
                    </div>
                    {openModule === m.id ? (
                      <ChevronUp className="w-5 h-5 text-slate-400" />
                    ) : (
                      <ChevronDown className="w-5 h-5 text-slate-400" />
                    )}
                  </button>
                  {openModule === m.id && m.description && (
                    <div className="px-5 pb-5 pt-0">
                      <p className="text-sm text-slate-600 leading-relaxed pl-12">{m.description}</p>
                    </div>
                  )}
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Skills */}
      {skills.length > 0 && (
        <section className="py-16 px-4 bg-white border-y border-slate-100">
          <div className="max-w-4xl mx-auto text-center">
            <h3 className="text-lg font-bold text-slate-900 mb-6">Skills You Will Master</h3>
            <div className="flex flex-wrap justify-center gap-2">
              {skills.map((s, i) => (
                <span key={i} className="px-3 py-1.5 rounded-full bg-slate-100 text-slate-700 text-sm font-medium">
                  {s}
                </span>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Pricing / CTA */}
      <section id="enroll" className="py-20 px-4 bg-white">
        <div className="max-w-3xl mx-auto">
          <Reveal>
            <div className="bg-slate-900 rounded-3xl p-8 sm:p-12 text-center text-white relative overflow-hidden">
              <div className="absolute top-0 right-0 w-64 h-64 bg-amber-500/10 rounded-full blur-3xl" />
              <div className="relative z-10">
                <Award className="w-12 h-12 text-amber-400 mx-auto mb-4" />
                <h2 className="text-3xl font-bold mb-3">Enroll in {course.title}</h2>
                <p className="text-slate-400 mb-8 max-w-lg mx-auto">
                  Get lifetime access to all modules, projects, and community. Certificate included.
                </p>

                <div className="flex items-center justify-center gap-4 mb-8">
                  {discountPrice > 0 && discountPrice < price ? (
                    <>
                      <span className="text-4xl font-bold">₹{discountPrice.toLocaleString()}</span>
                      <span className="text-xl text-slate-500 line-through">₹{price.toLocaleString()}</span>
                    </>
                  ) : (
                    <span className="text-4xl font-bold">₹{price.toLocaleString()}</span>
                  )}
                </div>

                <div className="flex flex-col sm:flex-row justify-center gap-3">
                  <Button onClick={handleEnroll} size="lg" leftIcon={<IndianRupee className="w-4 h-4" />}>
                    {course.is_enrolled ? 'Continue Learning' : 'Enroll Now'}
                  </Button>
                </div>

                <div className="flex items-center justify-center gap-6 mt-6 text-xs text-slate-500">
                  <span className="flex items-center gap-1"><Shield className="w-3 h-3" /> Secure Payment</span>
                  <span className="flex items-center gap-1"><CheckCircle className="w-3 h-3" /> Lifetime Access</span>
                  <span className="flex items-center gap-1"><Award className="w-3 h-3" /> Certificate</span>
                </div>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* FAQ */}
      {faqs.length > 0 && (
        <section id="faq" className="py-20 px-4 bg-slate-50">
          <div className="max-w-3xl mx-auto">
            <Reveal>
              <div className="text-center mb-12">
                <span className="inline-block px-4 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider bg-amber-50 text-amber-500 mb-4">
                  FAQ
                </span>
                <h2 className="text-3xl font-bold text-slate-900">Common Questions</h2>
              </div>
            </Reveal>

            <div className="space-y-3">
              {faqs.map((f, i) => (
                <motion.div
                  key={f.id}
                  initial={{ opacity: 0, y: 10 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.05 }}
                  className="bg-white rounded-2xl border border-slate-200 overflow-hidden"
                >
                  <button
                    onClick={() => setOpenFaq(openFaq === f.id ? null : f.id)}
                    className="w-full flex items-center justify-between p-5 text-left hover:bg-slate-50 transition-colors"
                  >
                    <span className="font-semibold text-slate-900 text-sm pr-4">{f.question}</span>
                    <span className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold transition-all ${openFaq === f.id ? 'bg-amber-500 text-white' : 'bg-slate-100 text-slate-500'}`}>
                      {openFaq === f.id ? '−' : '+'}
                    </span>
                  </button>
                  {openFaq === f.id && (
                    <div className="px-5 pb-5">
                      <p className="text-sm text-slate-600 leading-relaxed">{f.answer}</p>
                    </div>
                  )}
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      )}
      <LeadCapturePopup />
      <LeadCaptureFab />
      <LeadCaptureBanner />
    </div>
  );
}
